import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Delete, Cross, Wifi, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function Login() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const initialTab = params.get("mode") === "register" ? "owner" : "pin";
  const [tab, setTab] = useState(initialTab);
  const [code, setCode] = useState(localStorage.getItem("phCode") || "");
  const [pin, setPin] = useState("");
  const [emergency, setEmergency] = useState("");
  const [showEmergency, setShowEmergency] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ pharmacy_name: "", email: "", password: "", owner_name: "Titolare" });
  const [ip, setIp] = useState(null);
  const { loginPin, loginOwner, registerPharmacy } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/net/ip").then(({ data }) => setIp(data.ip)).catch(() => {});
  }, []);

  const submitPin = useCallback(async () => {
    if (!code.trim()) {
      setError("Inserisci prima il codice della farmacia");
      setPin("");
      return;
    }
    setBusy(true);
    try {
      await loginPin(code.trim().toLowerCase(), pin, emergency.trim());
      navigate("/dashboard");
    } catch (e) {
      setError(apiError(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [code, pin, emergency, loginPin, navigate]);

  useEffect(() => {
    if (pin.length === 4 && !busy) submitPin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const submitOwner = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await loginOwner(form.email, form.password);
      } else {
        await registerPharmacy({ ...form, terms_accepted: accepted });
        toast.success("Dati salvati. Completa l'attivazione con la carta.");
        navigate(`/attiva?plan=${params.get("plan") === "single_yearly" ? "single_yearly" : "single_monthly"}`);
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const digit = (d) => {
    setError("");
    if (pin.length < 4) setPin(pin + d);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-stretch">
      <div className="hidden lg:flex flex-col justify-between w-[42%] bg-emerald-700 p-14 text-white">
        <div className="flex items-center gap-3">
          <Cross className="h-7 w-7" strokeWidth={2.5} />
          <span className="font-head text-lg font-extrabold tracking-tight">FarmaPresenze</span>
        </div>
        <div>
          <h1 className="font-head text-4xl lg:text-5xl font-extrabold leading-[1.05]">
            Presenze del team,<br />sotto controllo.
          </h1>
          <p className="mt-6 text-emerald-100 text-base max-w-sm">
            Timbrature dal WiFi della farmacia, malattie, ferie e permessi. Ogni farmacia con il proprio account.
          </p>
        </div>
        <div className="text-xs text-emerald-200/90 font-mono-num flex items-center gap-2">
          <Wifi className="h-4 w-4" /> IP rilevato: <span data-testid="detected-ip">{ip || "..."}</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up" data-testid="login-panel">
          <div className="flex gap-2 mb-8">
            <button
              data-testid="tab-pin"
              onClick={() => { setTab("pin"); setError(""); }}
              className={`flex-1 h-11 rounded-lg text-sm font-semibold transition-colors ${
                tab === "pin" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-700"
              }`}
            >
              Operatore
            </button>
            <button
              data-testid="tab-owner"
              onClick={() => { setTab("owner"); setError(""); }}
              className={`flex-1 h-11 rounded-lg text-sm font-semibold transition-colors ${
                tab === "owner" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-700"
              }`}
            >
              Titolare
            </button>
          </div>

          {error && (
            <p data-testid="login-error" className="mb-5 text-sm font-medium text-red-600">{error}</p>
          )}

          {tab === "pin" ? (
            <div>
              <h2 className="font-head text-3xl font-extrabold">Inserisci il PIN</h2>
              <p className="text-sm text-gray-600 mt-1">
                Inserisci prima il codice della farmacia (resta memorizzato su questo dispositivo), poi le 4 cifre: l'accesso è automatico.
              </p>

              <input
                data-testid="pharmacy-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="codice farmacia (es. farmacia-demo)"
                className="mt-5 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
              <button data-testid="emergency-login-toggle" type="button" onClick={() => setShowEmergency(!showEmergency)} className="mt-3 text-xs text-emerald-700 underline">{showEmergency ? "Nascondi accesso di emergenza" : "Hai un token di emergenza?"}</button>
              {showEmergency && <label className="block mt-3 text-xs text-gray-600">Token del titolare (24 ore)<input data-testid="emergency-login-token" type="password" autoComplete="off" value={emergency} onChange={e => setEmergency(e.target.value)} className="block mt-1 w-full h-10 border border-gray-200 rounded-lg px-3 text-sm" /></label>}

              <div className="mt-6 flex gap-3" data-testid="pin-display">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`h-3.5 w-3.5 rounded-full border-2 ${
                      i < pin.length ? "bg-emerald-600 border-emerald-600" : "border-gray-300"
                    }`}
                  />
                ))}
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                  <button
                    key={d}
                    data-testid={`pin-digit-${d}`}
                    onClick={() => digit(String(d))}
                    className="h-16 rounded-xl bg-white border border-gray-200 shadow-sm font-mono-num text-2xl font-bold text-gray-900 transition-colors duration-200 hover:bg-emerald-50 active:scale-95 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  >
                    {d}
                  </button>
                ))}
                <button
                  data-testid="pin-clear"
                  onClick={() => { setPin(""); setError(""); }}
                  className="h-16 rounded-xl bg-white border border-gray-200 text-gray-600 text-sm font-semibold transition-colors hover:bg-gray-100 active:scale-95"
                >
                  Azzera
                </button>
                <button
                  data-testid="pin-digit-0"
                  onClick={() => digit("0")}
                  className="h-16 rounded-xl bg-white border border-gray-200 shadow-sm font-mono-num text-2xl font-bold transition-colors duration-200 hover:bg-emerald-50 active:scale-95"
                >
                  0
                </button>
                <button
                  data-testid="pin-backspace"
                  aria-label="Cancella ultima cifra"
                  onClick={() => setPin(pin.slice(0, -1))}
                  className="h-16 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 active:scale-95"
                >
                  <Delete className="h-5 w-5" />
                </button>
              </div>
              {busy && <p className="mt-4 text-sm text-gray-600">Accesso in corso...</p>}
            </div>
          ) : (
            <form onSubmit={submitOwner} className="space-y-4">
              <h2 className="font-head text-3xl font-extrabold">
                {mode === "login" ? "Accesso titolare" : "Registra la farmacia"}
              </h2>
              {mode === "register" && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-600">Nome farmacia</label>
                  <input
                    data-testid="register-pharmacy-name"
                    required
                    value={form.pharmacy_name}
                    onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    placeholder="Farmacia San Marco"
                  />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-600">Email</label>
                <input
                  data-testid="owner-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-600">Password</label>
                <input
                  data-testid="owner-password"
                  type="password"
                  required
                  minLength={mode === "register" ? 8 : undefined}
                  maxLength={72}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
              {mode === "register" && <>
                <p data-testid="register-pricing" className="text-sm p-3 border-l-2 border-emerald-600 bg-emerald-50 text-emerald-900">30 giorni gratis con carta · 0,00€ oggi.<br />Poi {params.get("plan") === "single_yearly" ? "290€/anno" : "29€/mese"}, IVA esclusa se applicabile. Disdetta gratuita prima della scadenza.</p>
                <label className="flex items-start gap-2 text-xs text-gray-600"><input data-testid="register-terms" type="checkbox" required checked={accepted} onChange={e => setAccepted(e.target.checked)} className="mt-1 accent-emerald-600" /><span>Accetto i <Link data-testid="register-terms-link" to="/termini" target="_blank" className="underline">Termini del Servizio</Link> e il rinnovo automatico. Ho letto la <Link data-testid="register-privacy-link" to="/privacy" target="_blank" className="underline">Privacy Policy</Link>.</span></label>
              </>}
              <button
                data-testid="owner-submit"
                disabled={busy}
                className="w-full h-12 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95 disabled:opacity-60"
              >
                {busy ? "Attendi…" : mode === "login" ? "Accedi" : "Continua · Inserisci carta"} <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="toggle-register"
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="w-full text-sm text-emerald-700 font-medium hover:underline"
              >
                {mode === "login" ? "Non hai un account? Registra la tua farmacia" : "Hai già un account? Accedi"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
